import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { apiPath, baseUrl } from 'config'
import {
  AddressString,
  AllDepositWithdrawalAssetRequest,
  DepositWithdrawalAssetRequest,
  DepositWithdrawalAssetResponse,
  KycStatusResponseDTO,
} from 'types'

import { setDepositAssetsChangesData, setWithdrawalAssetsChangesData } from 'store/reducers-common/Pool/slice.ts'
import { getSignature } from 'utils/getSignature'

export const queryAssets = createApi({
  baseQuery: fetchBaseQuery({ baseUrl }),
  endpoints: (builder) => ({
    allAssetChanges: builder.query<DepositWithdrawalAssetResponse, AllDepositWithdrawalAssetRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { expirationDateOrder, limit, minExpirationDate, order, page, status, type } = arg
        const statusParam = Array.isArray(status)
          ? status.reduce((acc, curr) => `${acc}&status=${curr}`, '')
          : `&status=${status}`

        let url = `${apiPath.allAssetChanges}?type=${type}&limit=${limit}&page=${page}${statusParam}`

        if (minExpirationDate) {
          const isoDate = minExpirationDate.toISOString()
          url = url.concat(`&minExpirationDate=${isoDate}`)
        }
        if (order) {
          url = url.concat(`&order=${order}`)
        } else if (expirationDateOrder) {
          url = url.concat(`&expirationDateOrder=${expirationDateOrder}`)
        }

        const { deadline, signature } = await getSignature({
          primaryType: 'GetAllAssetChanges',
          url,
        })

        const result = await baseQuery({
          headers: { deadline: `${deadline}`, signature },
          url,
        })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }
        const data = result.data as DepositWithdrawalAssetResponse
        _api.dispatch(setWithdrawalAssetsChangesData(data))
        return { data }
      },
    }),
    assetChange: builder.query<DepositWithdrawalAssetResponse, DepositWithdrawalAssetRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { expirationDateOrder, limit, minExpirationDate, order, page, poolAddress, status, type } = arg
        const statusParam = Array.isArray(status)
          ? status.reduce((acc, curr) => `${acc}&status=${curr}`, '')
          : `&status=${status}`

        let url = `${apiPath.assetsChanges(poolAddress)}?type=${type}&limit=${limit}${order ? `&order=${order}` : ``}${expirationDateOrder ? `&expirationDateOrder=${expirationDateOrder}` : ``}&page=${page}${statusParam}`

        if (minExpirationDate) {
          const isoDate = minExpirationDate.toISOString()
          url = url.concat(`&minExpirationDate=${isoDate}`)
        }

        const result = await baseQuery({ url })

        if (!result.data) {
          return { error: result.error as FetchBaseQueryError }
        }
        const data = result.data as DepositWithdrawalAssetResponse
        if (type === 'Withdrawal') {
          _api.dispatch(setWithdrawalAssetsChangesData(data))
        } else {
          _api.dispatch(setDepositAssetsChangesData(data))
        }
        return { data }
      },
    }),
    assetChangeKyc: builder.query<
      KycStatusResponseDTO,
      { account: AddressString; poolAddress: AddressString; requestId: string }
    >({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        const { poolAddress, requestId } = arg
        const url = apiPath.assetChangeKyc(poolAddress, requestId)
        const { deadline, signature } = await getSignature({ primaryType: 'GetAssetChangeKyc', url })
        const result = await baseQuery({ headers: { deadline: `${deadline}`, signature }, url })

        if (result.error) return { error: result.error }
        return { data: result.data as KycStatusResponseDTO }
      },
    }),
  }),
  reducerPath: 'assets',
})
export const { useAllAssetChangesQuery, useAssetChangeQuery, useLazyAssetChangeKycQuery, useLazyAssetChangeQuery } =
  queryAssets
